import classNames from 'classnames';
import { CSSProperties, Children, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { ChevronDownBold } from 'atoms/icons/ChevronDownBold';
import { LabelS } from 'atoms/typography';
import { useTransitionApi } from 'hooks/ui/useTransitionApi';
import { glossary } from 'lib/glossary';

import { Horizontal, Vertical } from '../flex';

const Root = styled(Vertical)`
  gap: var(--double-unit);
  cursor: pointer;
  position: relative;
  isolation: isolate;

  &.expanded {
    cursor: initial;
  }
`;
const ShowLessWrapper = styled(Horizontal)`
  justify-content: flex-end;
  transition: all 0.5s;
  opacity: 0;
  margin-top: calc(-1 * var(--quadruple-unit));
  z-index: -1;
  pointer-events: none;

  .expanded & {
    opacity: 1;
    margin-top: 0;
    pointer-events: auto;
  }
`;

const Grid = styled.div`
  position: relative;
  isolation: isolate;
  display: grid;
  align-items: flex-end;
  &.expanded {
    gap: var(--double-unit);
  }
  &:not(.expanded) {
    grid-template-rows: 1fr var(--unit);
    > * {
      grid-area: 1 / 1;
      transform-origin: bottom;
      &:nth-child(n + 2) {
        grid-area: 2 / 1;
        transform: scale(0.95);
        overflow: hidden;
        max-height: 100%;
        scroll-snap-type: y mandatory;
      }
      &:nth-child(2) {
        height: 350px;
      }
    }
    &:has(> :nth-child(3)) {
      grid-template-rows: 1fr var(--unit) var(--unit);
      > *:nth-child(n + 3) {
        transform: scale(0.9);
        grid-area: 3 / 1;
      }
    }
  }
`;

const AdaptPosition = styled.div`
  /** While there is a discussion around the scroll-start spec, we can use a workaround to force
   * the block alignement caused by the max-height/overflow to be the bottom instead of the top using scroll-snap-type
   */
  position: absolute;
  width: 1px;
  visibility: hidden;
  &::before {
    content: '';
    height: 1px;
    display: block;
    scroll-snap-align: start;
  }
`;

const StyledHorizontal = styled(Horizontal)`
  align-self: flex-end;
  justify-content: flex-end;
`;
const ChevronUp = styled(ChevronDownBold)`
  transform: rotate(180deg);
`;

type ShowLessProps = {
  onClick: () => void;
};
const ShowLess = ({ onClick }: ShowLessProps) => {
  return (
    <ButtonBase onClick={onClick}>
      <StyledHorizontal gap={0.5}>
        <ChevronUp />
        <LabelS color="var(--c-nd-800)">
          <FormattedMessage {...glossary.showLess} />
        </LabelS>
      </StyledHorizontal>
    </ButtonBase>
  );
};

type Props = {
  children: ReactNode;
  startsCollapsed?: boolean;
  hideTop?: boolean;
};
export const CollapsibleTileContainer = ({
  children,
  startsCollapsed = true,
  hideTop,
}: Props) => {
  const { updateDOM } = useTransitionApi();
  const [expanded, setExpanded] = useState(!startsCollapsed);
  const childrenArray = Children.toArray(children);

  if (childrenArray.length === 1) {
    return children;
  }

  return (
    <Root
      className={classNames({ expanded })}
      onClickCapture={e => {
        if (!expanded) {
          e.stopPropagation();
          e.preventDefault();
          updateDOM(() => setExpanded(true));
        }
      }}
    >
      {!hideTop && (
        <ShowLessWrapper>
          <ShowLess
            onClick={() => {
              updateDOM(() => setExpanded(false));
            }}
          />
        </ShowLessWrapper>
      )}
      <Grid className={classNames({ expanded })}>
        {childrenArray.map((child, index) => (
          <div
            style={
              {
                viewTransitionName: `tile-${index}`,
                zIndex: childrenArray.length - index,
              } as CSSProperties
            }
            // eslint-disable-next-line react/no-array-index-key
            key={
              typeof child === 'object' && 'key' in child ? child.key : index
            }
          >
            {child}
            <AdaptPosition />
          </div>
        ))}
      </Grid>
      {expanded && (
        <ShowLess
          onClick={() => {
            updateDOM(() => setExpanded(false));
          }}
        />
      )}
    </Root>
  );
};
